import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import {
  Experiment,
  initializeWithAmplitudeAnalytics,
} from "@amplitude/experiment-js-client";
import { v4 as uuidv4 } from "uuid";

const GLOBAL_USER_ID_KEY = "amplitude_global_user_id";

let isInitialized = false;
let experimentClient: Experiment | null = null;

/**
 * Get or create a persistent anonymous user ID (globalUserId).
 * This ID is used to track anonymous users before they authenticate.
 */
export function getGlobalUserId(): string {
  let globalUserId = localStorage.getItem(GLOBAL_USER_ID_KEY);
  if (!globalUserId) {
    globalUserId = uuidv4();
    localStorage.setItem(GLOBAL_USER_ID_KEY, globalUserId);
  }
  return globalUserId;
}

interface InitAmplitudeOptions {
  enableSessionReplay?: boolean;
}

/**
 * Initialize Amplitude analytics with optional session replay.
 * @param apiKey - The Amplitude API key
 * @param globalUserId - The anonymous user ID (used as device_id for anonymous tracking)
 * @param options - Additional options (e.g., enableSessionReplay)
 */
export function initAmplitude(
  apiKey: string,
  globalUserId?: string,
  options: InitAmplitudeOptions = {},
): void {
  if (isInitialized || !apiKey) {
    return;
  }

  const { enableSessionReplay = false } = options;

  // Only add session replay if explicitly enabled
  // Session replay requires additional configuration in Amplitude dashboard
  if (enableSessionReplay) {
    const sessionReplayTracking = sessionReplayPlugin();
    amplitude.add(sessionReplayTracking);
  }

  amplitude.init(apiKey, undefined, {
    autocapture: true,
    serverZone: "EU",
    deviceId: globalUserId,
    defaultTracking: {
      sessions: true,
      pageViews: true,
      formInteractions: true,
      fileDownloads: false,
    },
  });

  isInitialized = true;
}

/**
 * Track an event in Amplitude.
 * @param eventName - The name of the event to track
 * @param eventProperties - Optional properties to include with the event
 */
export function trackEvent(
  eventName: string,
  eventProperties?: Record<string, unknown>,
): void {
  if (!isInitialized) {
    console.warn("Amplitude is not initialized. Event not tracked:", eventName);
    return;
  }

  amplitude.track(eventName, eventProperties);
}

/**
 * Identify the user after authentication.
 * This links anonymous events to the authenticated user.
 * @param backendUserId - The user ID from the backend
 */
export function identifyUser(backendUserId: string): void {
  if (!isInitialized) {
    console.warn("Amplitude is not initialized. User not identified.");
    return;
  }

  amplitude.setUserId(backendUserId);
}

/**
 * Reset the user identity (for logout).
 */
export function resetUser(): void {
  if (!isInitialized) {
    return;
  }

  amplitude.reset();
}

/**
 * Check if Amplitude is initialized.
 */
export function isAmplitudeInitialized(): boolean {
  return isInitialized;
}

/**
 * Initialize Amplitude Experiment client for A/B testing.
 * Should be called after initAmplitude().
 * @param deploymentKey - The Amplitude Experiment deployment key
 */
export function initExperiment(deploymentKey: string): void {
  if (!deploymentKey) {
    console.warn("Experiment deployment key not provided");
    return;
  }

  if (experimentClient) {
    console.warn("Experiment client already initialized");
    return;
  }

  try {
    experimentClient = initializeWithAmplitudeAnalytics(deploymentKey, {
      serverZone: "EU",
      automaticExposureTracking: true,
    });
  } catch (error) {
    console.error("Failed to initialize Amplitude Experiment:", error);
  }
}

/**
 * Fetch experiment variants from Amplitude.
 * @returns Promise that resolves when variants are fetched
 */
export async function fetchExperimentVariants(): Promise<void> {
  if (!experimentClient) {
    throw new Error("Experiment client not initialized");
  }

  await experimentClient.fetch();
}

/**
 * Get a variant for a specific experiment flag.
 * @param flagKey - The experiment flag key
 * @returns The variant value, or null if not found
 */
export function getVariant(flagKey: string): string | null {
  if (!experimentClient) {
    console.warn("Experiment client not initialized");
    return null;
  }

  const variant = experimentClient.variant(flagKey);
  return variant.value || null;
}

export { amplitude, experimentClient };
